import { useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useSetup } from '@/lib/auth';
import { SetupRequestSchema, type SetupRequest } from '@/lib/api';
import { z } from 'zod';

/**
 * Setup wizard en 3 pasos (identidad / métricas / preferencias+passphrase).
 *
 * Decisiones:
 * - Un único form que mantiene el estado y enseña el subset correspondiente al
 *   paso actual. Submit real solo en el último paso.
 * - Validación con Zod (`SetupRequestSchema`) al hacer submit final + una
 *   validación local mínima para mostrar errores por paso.
 * - Tras éxito, el AppRouter redirige automáticamente al detectar
 *   `authenticated === true` en la query de auth.
 */

type StepIndex = 0 | 1 | 2;

type FormState = {
  name: string;
  age: string;
  sex: '' | 'M' | 'F' | 'X';
  weightKg: string;
  heightCm: string;
  ftpW: string;
  lthrBpm: string;
  tPaceRun: string;
  tPaceSwim: string;
  timezone: string;
  units: 'metric' | 'imperial';
  yearGoal: string;
  passphrase: string;
  passphraseConfirm: string;
};

const INITIAL_STATE: FormState = {
  name: '',
  age: '',
  sex: '',
  weightKg: '',
  heightCm: '',
  ftpW: '',
  lthrBpm: '',
  tPaceRun: '',
  tPaceSwim: '',
  timezone: 'Europe/Madrid',
  units: 'metric',
  yearGoal: '',
  passphrase: '',
  passphraseConfirm: '',
};

function optionalNumber(s: string): number | null | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.NaN; // NaN forzará error en Zod
}

function optionalInt(s: string): number | null | undefined {
  if (s.trim() === '') return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : Number.NaN;
}

function optionalString(s: string): string | null | undefined {
  const trimmed = s.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Indicador simple de fuerza basado en longitud + variedad de clases de
 * caracteres. No es un análisis criptográfico — es feedback UX.
 */
function passphraseStrength(p: string): { label: string; score: 0 | 1 | 2 | 3 | 4; color: string } {
  if (p.length < 12) return { label: 'Demasiado corta', score: 0, color: 'bg-rose-500' };
  let variety = 0;
  if (/[a-z]/.test(p)) variety++;
  if (/[A-Z]/.test(p)) variety++;
  if (/[0-9]/.test(p)) variety++;
  if (/[^a-zA-Z0-9]/.test(p)) variety++;
  const lengthBonus = p.length >= 20 ? 1 : 0;
  const score = Math.min(4, variety + lengthBonus) as 0 | 1 | 2 | 3 | 4;
  const labels = ['Débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte'];
  const colors = ['bg-rose-500', 'bg-rose-500', 'bg-amber-500', 'bg-teal-500', 'bg-emerald-600'];
  return { label: labels[score], score, color: colors[score] };
}

function buildSetupBody(state: FormState): SetupRequest {
  return {
    name: state.name.trim(),
    age: optionalInt(state.age) ?? null,
    sex: state.sex === '' ? null : state.sex,
    weightKg: optionalNumber(state.weightKg) ?? null,
    heightCm: optionalNumber(state.heightCm) ?? null,
    ftpW: optionalInt(state.ftpW) ?? null,
    lthrBpm: optionalInt(state.lthrBpm) ?? null,
    tPaceRun: optionalString(state.tPaceRun) ?? null,
    tPaceSwim: optionalString(state.tPaceSwim) ?? null,
    timezone: state.timezone.trim() || 'Europe/Madrid',
    units: state.units,
    yearGoal: optionalString(state.yearGoal) ?? null,
    passphrase: state.passphrase,
  } as SetupRequest;
}

export function SetupWizard(): ReactElement {
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [step, setStep] = useState<StepIndex>(0);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const setupMutation = useSetup();
  const formId = useId();

  const strength = useMemo(() => passphraseStrength(state.passphrase), [state.passphrase]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validateStep(s: StepIndex): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (s === 0) {
      if (!state.name.trim()) errors.name = 'El nombre es obligatorio';
      if (state.age && !/^\d+$/.test(state.age.trim())) errors.age = 'Debe ser un número entero';
    }
    if (s === 1) {
      const paceRegex = /^[0-9]{1,2}:[0-5][0-9]$/;
      if (state.tPaceRun.trim() && !paceRegex.test(state.tPaceRun.trim())) {
        errors.tPaceRun = 'Formato esperado mm:ss';
      }
      if (state.tPaceSwim.trim() && !paceRegex.test(state.tPaceSwim.trim())) {
        errors.tPaceSwim = 'Formato esperado mm:ss';
      }
    }
    if (s === 2) {
      if (state.passphrase.length < 12) errors.passphrase = 'Mínimo 12 caracteres';
      if (state.passphrase !== state.passphraseConfirm) {
        errors.passphraseConfirm = 'Las passphrases no coinciden';
      }
      if (!state.timezone.trim()) errors.timezone = 'Timezone obligatoria';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (step < 2) {
      if (validateStep(step)) setStep((step + 1) as StepIndex);
      return;
    }
    // Paso final.
    if (!validateStep(2)) return;
    const body = buildSetupBody(state);
    const result = SetupRequestSchema.safeParse(body);
    if (!result.success) {
      // Mapear errores de Zod a fieldErrors por la primera key
      const errors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of (result.error as z.ZodError).issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }
    setupMutation.mutate(result.data);
  }

  const steps = ['Identidad', 'Métricas', 'Preferencias'] as const;

  return (
    <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Configura tu cuenta</h2>
        <p className="mt-1 text-sm text-slate-600">
          Vamos a crear tu perfil. Sólo el nombre y la passphrase son obligatorios.
        </p>
      </header>

      <ol role="list" className="flex items-center justify-between text-xs">
        {steps.map((label, idx) => {
          const active = idx === step;
          const done = idx < step;
          return (
            <li
              key={label}
              aria-current={active ? 'step' : undefined}
              className="flex flex-1 items-center gap-2"
            >
              <span
                aria-hidden="true"
                className={
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ' +
                  (active
                    ? 'bg-teal-600 text-white'
                    : done
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-200 text-slate-600')
                }
              >
                {idx + 1}
              </span>
              <span className={active ? 'font-medium text-slate-900' : 'text-slate-500'}>
                {label}
              </span>
              {idx < steps.length - 1 && (
                <span aria-hidden="true" className="ml-1 mr-1 h-px flex-1 bg-slate-200" />
              )}
            </li>
          );
        })}
      </ol>

      {step === 0 && <StepIdentity state={state} update={update} errors={fieldErrors} />}
      {step === 1 && <StepMetrics state={state} update={update} errors={fieldErrors} />}
      {step === 2 && (
        <StepPreferences
          state={state}
          update={update}
          errors={fieldErrors}
          strength={strength}
        />
      )}

      {setupMutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo completar el setup. Comprueba los datos o reintenta.
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setStep((Math.max(0, step - 1)) as StepIndex)}
          disabled={step === 0 || setupMutation.isPending}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Atrás
        </button>
        <button
          type="submit"
          disabled={setupMutation.isPending}
          aria-busy={setupMutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {step < 2 ? 'Siguiente' : setupMutation.isPending ? 'Creando…' : 'Crear cuenta'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Subcomponentes de pasos
// ---------------------------------------------------------------------------

type StepProps = {
  state: FormState;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  errors: Partial<Record<keyof FormState, string>>;
};

function Field({
  id,
  label,
  error,
  children,
  hint,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: ReactElement;
}): ReactElement {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}

function StepIdentity({ state, update, errors }: StepProps): ReactElement {
  return (
    <fieldset className="space-y-4">
      <legend className="sr-only">Identidad</legend>

      <Field id="name" label="Nombre *" error={errors.name}>
        <input
          id="name"
          type="text"
          required
          autoComplete="name"
          value={state.name}
          onChange={(e) => update('name', e.target.value)}
          aria-invalid={errors.name !== undefined}
          aria-describedby={errors.name ? 'name-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field id="age" label="Edad" error={errors.age}>
        <input
          id="age"
          type="number"
          inputMode="numeric"
          min={10}
          max={120}
          value={state.age}
          onChange={(e) => update('age', e.target.value)}
          aria-invalid={errors.age !== undefined}
          aria-describedby={errors.age ? 'age-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field id="sex" label="Sexo">
        <select
          id="sex"
          value={state.sex}
          onChange={(e) => update('sex', e.target.value as FormState['sex'])}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        >
          <option value="">— sin especificar —</option>
          <option value="M">Masculino</option>
          <option value="F">Femenino</option>
          <option value="X">Otro / prefiero no decir</option>
        </select>
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field id="weightKg" label="Peso (kg)" error={errors.weightKg}>
          <input
            id="weightKg"
            type="number"
            inputMode="decimal"
            step="0.1"
            min={1}
            max={400}
            value={state.weightKg}
            onChange={(e) => update('weightKg', e.target.value)}
            aria-invalid={errors.weightKg !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>

        <Field id="heightCm" label="Talla (cm)" error={errors.heightCm}>
          <input
            id="heightCm"
            type="number"
            inputMode="numeric"
            step="1"
            min={1}
            max={280}
            value={state.heightCm}
            onChange={(e) => update('heightCm', e.target.value)}
            aria-invalid={errors.heightCm !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>
      </div>
    </fieldset>
  );
}

function StepMetrics({ state, update, errors }: StepProps): ReactElement {
  return (
    <fieldset className="space-y-4">
      <legend className="sr-only">Métricas de entrenamiento</legend>
      <p className="text-xs text-slate-600">
        Todos los campos son opcionales. Si no los conoces, los calcularemos con tus datos de Strava
        más adelante.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <Field id="ftpW" label="FTP (W)" error={errors.ftpW}>
          <input
            id="ftpW"
            type="number"
            inputMode="numeric"
            min={1}
            max={2000}
            value={state.ftpW}
            onChange={(e) => update('ftpW', e.target.value)}
            aria-invalid={errors.ftpW !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>

        <Field id="lthrBpm" label="LTHR (bpm)" error={errors.lthrBpm}>
          <input
            id="lthrBpm"
            type="number"
            inputMode="numeric"
            min={1}
            max={260}
            value={state.lthrBpm}
            onChange={(e) => update('lthrBpm', e.target.value)}
            aria-invalid={errors.lthrBpm !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>
      </div>

      <Field
        id="tPaceRun"
        label="T-pace running"
        error={errors.tPaceRun}
        hint="mm:ss por km — p.ej. 4:30"
      >
        <input
          id="tPaceRun"
          type="text"
          inputMode="numeric"
          placeholder="4:30"
          value={state.tPaceRun}
          onChange={(e) => update('tPaceRun', e.target.value)}
          aria-invalid={errors.tPaceRun !== undefined}
          aria-describedby={errors.tPaceRun ? 'tPaceRun-error' : 'tPaceRun-hint'}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field
        id="tPaceSwim"
        label="T-pace swimming"
        error={errors.tPaceSwim}
        hint="mm:ss por 100m — p.ej. 1:35"
      >
        <input
          id="tPaceSwim"
          type="text"
          inputMode="numeric"
          placeholder="1:35"
          value={state.tPaceSwim}
          onChange={(e) => update('tPaceSwim', e.target.value)}
          aria-invalid={errors.tPaceSwim !== undefined}
          aria-describedby={errors.tPaceSwim ? 'tPaceSwim-error' : 'tPaceSwim-hint'}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>
    </fieldset>
  );
}

function StepPreferences({
  state,
  update,
  errors,
  strength,
}: StepProps & { strength: ReturnType<typeof passphraseStrength> }): ReactElement {
  return (
    <fieldset className="space-y-4">
      <legend className="sr-only">Preferencias y passphrase</legend>

      <Field id="timezone" label="Timezone" error={errors.timezone}>
        <input
          id="timezone"
          type="text"
          value={state.timezone}
          onChange={(e) => update('timezone', e.target.value)}
          aria-invalid={errors.timezone !== undefined}
          aria-describedby={errors.timezone ? 'timezone-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <fieldset>
        <legend className="block text-sm font-medium text-slate-700">Unidades</legend>
        <div className="mt-1 flex gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="units"
              value="metric"
              checked={state.units === 'metric'}
              onChange={() => update('units', 'metric')}
            />
            Métrico (km, kg)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="units"
              value="imperial"
              checked={state.units === 'imperial'}
              onChange={() => update('units', 'imperial')}
            />
            Imperial (mi, lb)
          </label>
        </div>
      </fieldset>

      <Field id="yearGoal" label="Objetivo del año">
        <textarea
          id="yearGoal"
          rows={3}
          maxLength={2000}
          value={state.yearGoal}
          onChange={(e) => update('yearGoal', e.target.value)}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field id="passphrase" label="Passphrase *" error={errors.passphrase} hint="Mínimo 12 caracteres">
        <input
          id="passphrase"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          value={state.passphrase}
          onChange={(e) => update('passphrase', e.target.value)}
          aria-invalid={errors.passphrase !== undefined}
          aria-describedby={errors.passphrase ? 'passphrase-error passphrase-strength' : 'passphrase-hint passphrase-strength'}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <div id="passphrase-strength" aria-live="polite" className="text-xs text-slate-600">
        <div className="flex h-1.5 w-full overflow-hidden rounded bg-slate-200" aria-hidden="true">
          <div
            className={`h-full transition-all ${strength.color}`}
            style={{ width: `${(strength.score / 4) * 100}%` }}
          />
        </div>
        <p className="mt-1">Fuerza: {strength.label}</p>
      </div>

      <Field
        id="passphraseConfirm"
        label="Confirmar passphrase *"
        error={errors.passphraseConfirm}
      >
        <input
          id="passphraseConfirm"
          type="password"
          autoComplete="new-password"
          required
          value={state.passphraseConfirm}
          onChange={(e) => update('passphraseConfirm', e.target.value)}
          aria-invalid={errors.passphraseConfirm !== undefined}
          aria-describedby={errors.passphraseConfirm ? 'passphraseConfirm-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>
    </fieldset>
  );
}
